import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Play, Calculator, Info, Download, Share2 } from "lucide-react";
import { downloadToClientVault } from "@/lib/localVault";

const brl = (cents) => (Number(cents || 0) / 100).toLocaleString("pt-BR",
  { style: "currency", currency: "BRL" });

function buildShareUrl(publicToken) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${base}/api/public/liq/calc/${publicToken}/share.html`;
}

async function shareCalcOnWhatsApp(calc, caseData) {
  const token = calc?.public_token;
  if (!token) {
    toast.error("Este cálculo não tem selo público ainda. Gere um novo cálculo.");
    return;
  }
  const url = buildShareUrl(token);
  const process = caseData?.process_number || "";
  const total = brl(calc.total_updated_cents || calc.total_cents);
  const msg = `Memória de cálculo trabalhista${process ? ` · ${process}` : ""}\nTotal: ${total}\n${url}`;
  try { await navigator.clipboard.writeText(url); } catch {}
  toast.success("Link copiado! Abrindo WhatsApp…");
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
}

function monthRange(start, end) {
  if (!start || !end) return [];
  const [y0, m0] = start.split("-").map(Number);
  const [y1, m1] = end.split("-").map(Number);
  const out = [];
  let y = y0, m = m0;
  while (y < y1 || (y === y1 && m <= m1)) {
    out.push(`${y}-${String(m).padStart(2,"0")}`);
    m++; if (m > 12) { m = 1; y++; }
    if (out.length > 240) break;   // safety
  }
  return out;
}

export default function LiquidacaoCalculo() {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [params, setParams] = useState([]);
  const [calcStart, setCalcStart] = useState("");
  const [calcEnd, setCalcEnd] = useState("");
  const [qtyByMonth, setQtyByMonth] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    const [c, p, h] = await Promise.all([
      api.get(`/liq/cases/${id}`),
      api.get(`/liq/cases/${id}/parameters`),
      api.get(`/liq/cases/${id}/calculations`),
    ]);
    setCaseData(c.data);
    setParams(p.data.rows || []);
    setHistory(h.data.rows || []);
    if (c.data.calculation_start && !calcStart) setCalcStart(c.data.calculation_start.slice(0,7));
    if (c.data.calculation_end && !calcEnd) setCalcEnd(c.data.calculation_end.slice(0,7));
  }, [id, calcStart, calcEnd]);

  useEffect(() => { load(); }, []);

  const months = useMemo(() => monthRange(calcStart, calcEnd), [calcStart, calcEnd]);

  const blockers = params.filter(p =>
    p.material && ["MISSING", "REVIEW_REQUIRED", "CONFLICTING"].includes(p.status)
  );
  const canRun = blockers.length === 0 && months.length > 0 &&
    Object.values(qtyByMonth).some(v => Number(v) > 0);

  const salaryCents = params.find(p => p.key === "salary.base_amount_cents")?.value ?? 0;
  const divisor = params.find(p => p.key === "overtime.divisor")?.value ?? 220;
  const multiplier = params.find(p => p.key === "overtime.multiplier")?.value ?? 1.5;

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/liq/cases/${id}/calculate`, {
        calc_start: calcStart, calc_end: calcEnd,
        verbas: [{ code: "horas_extras", label: "Horas extras",
                     quantity_by_month: Object.fromEntries(
                       Object.entries(qtyByMonth).map(([k,v]) => [k, Number(v) || 0])
                     ) }],
      });
      if (r.data.ok) {
        setResult(r.data.calculation);
        toast.success(`Cálculo concluído · Total ${brl(r.data.calculation.total_cents)}`);
        load();
      } else {
        toast.error(`Cálculo bloqueado: ${(r.data.blockers || []).map(b => b.label).join(", ")}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no cálculo");
    } finally { setRunning(false); }
  };

  const downloadPdf = async (calcId) => {
    try {
      const token = (document.cookie.match(/session_token=([^;]+)/) || [])[1];
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const client = caseData?.claimant?.name || caseData?.process_number || "Cliente";
      const filename = `memoria-calculo-${caseData?.process_number || calcId.slice(0,8)}.pdf`;
      const r = await downloadToClientVault({
        url: `${API}/liq/calculations/${calcId}/pdf`,
        headers, client, subfolder: "Cálculos", filename,
      });
      toast.success(r.mode === "vault"
        ? `Salvo em Hermes/${r.path}`
        : "Baixado para a pasta Downloads (configure o cofre local no /perfil).");
    } catch (e) {
      toast.error(e?.message || "Falha ao gerar PDF");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-6" data-testid="liq-calc">
      <div className="max-w-6xl mx-auto space-y-4">
        <Link to={`/calculos/liquidacao-trabalhista/${id}`}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="liq-calc-back">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar ao caso
        </Link>

        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Hermes Calc · Cálculo (v0.1)
          </div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">
            Horas extras — competência a competência
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Fase 6 · motor determinístico em Decimal. Fase 7 · rubrica "horas extras" pronta.
            Correção monetária, juros e demais rubricas entram nas próximas fases.
          </p>
        </div>

        {/* Bloqueadores */}
        {blockers.length > 0 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="liq-calc-blockers">
            <div className="flex items-start gap-2 text-sm text-rose-900">
              <Info className="w-4 h-4 mt-0.5"/>
              <div>
                <b>Cálculo bloqueado.</b> Existem parâmetros materiais MISSING ou
                em conflito. Resolva antes de calcular:
                <ul className="mt-1 list-disc pl-5">
                  {blockers.map(b => <li key={b.key}><b>{b.label}</b> ({b.status})</li>)}
                </ul>
                <Link to={`/calculos/liquidacao-trabalhista/${id}/parametros`}
                  className="mt-2 inline-block underline font-semibold text-rose-800"
                  data-testid="liq-calc-goto-params">
                  Ir para parâmetros
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Parâmetros vigentes */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ParamDisplay label="Salário-base"     value={brl(salaryCents)} testid="pd-base"/>
          <ParamDisplay label="Divisor"           value={divisor}          testid="pd-divisor"/>
          <ParamDisplay label="Multiplicador"     value={multiplier}       testid="pd-mult"/>
          <ParamDisplay label="Hora normal"
                        value={brl((Number(salaryCents)/Number(divisor)))}
                        testid="pd-hour"/>
        </div>

        {/* Período */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            Período de apuração
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="block">
              <span className="text-xs text-slate-500">Início</span>
              <input type="month" value={calcStart}
                onChange={e => setCalcStart(e.target.value)}
                className="mt-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm"
                data-testid="liq-calc-start"/>
            </label>
            <label className="block">
              <span className="text-xs text-slate-500">Fim</span>
              <input type="month" value={calcEnd}
                onChange={e => setCalcEnd(e.target.value)}
                className="mt-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm"
                data-testid="liq-calc-end"/>
            </label>
            <div className="text-xs text-slate-500 self-center">
              {months.length} competência{months.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Quantidade por mês */}
        {months.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">
              Horas extras por competência
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {months.map(ym => (
                <label key={ym} className="block">
                  <span className="text-[10px] uppercase text-slate-500">{ym}</span>
                  <input type="number" min="0" step="0.5"
                    value={qtyByMonth[ym] ?? ""}
                    onChange={e => setQtyByMonth(q => ({...q, [ym]: e.target.value}))}
                    placeholder="0"
                    className="mt-0.5 w-full h-9 px-2 rounded-md border border-slate-200 text-sm font-mono"
                    data-testid={`liq-calc-qty-${ym}`}/>
                </label>
              ))}
            </div>
          </div>
        )}

        <button onClick={run} disabled={!canRun || running}
          data-testid="liq-calc-run"
          className="h-12 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium inline-flex items-center gap-2">
          <Play className="w-4 h-4"/>
          {running ? "Calculando…" : "Executar cálculo"}
        </button>

        {/* Resultado */}
        {result && (
          <div className="rounded-2xl border border-emerald-300 bg-white p-5" data-testid="liq-calc-result">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                <Calculator className="w-6 h-6"/>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Total apurado</div>
                <div className="text-3xl font-bold text-slate-900">{brl(result.total_updated_cents || result.total_cents)}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  motor {result.engine_version} · gerado em {new Date(result.generated_at).toLocaleString("pt-BR")}
                </div>
              </div>
              <button onClick={() => downloadPdf(result.id)}
                data-testid="liq-calc-download-pdf"
                className="h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
                <Download className="w-4 h-4"/> Baixar memória (PDF)
              </button>
              <button onClick={() => shareCalcOnWhatsApp(result, caseData)}
                data-testid="liq-calc-share-wa"
                className="h-11 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2"
                title="Compartilhar link com preview no WhatsApp">
                <Share2 className="w-4 h-4"/> WhatsApp
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {result.verbas.map(v => (
                <div key={v.code}>
                  <div className="text-sm font-semibold text-slate-800">
                    {v.label} — <span className="text-emerald-700">{brl(v.total_cents)}</span>
                  </div>
                  <table className="mt-2 w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                        <th className="text-left py-1">Competência</th>
                        <th className="text-right py-1">Horas</th>
                        <th className="text-right py-1">Base÷Div</th>
                        <th className="text-right py-1">× Mult</th>
                        <th className="text-right py-1">Devido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.rows.map(r => (
                        <tr key={r.competence} className="border-b border-slate-100">
                          <td className="py-1 font-mono">{r.competence}</td>
                          <td className="py-1 text-right font-mono">{r.quantity}</td>
                          <td className="py-1 text-right font-mono">{brl(r.base_cents)} ÷ {r.divisor}</td>
                          <td className="py-1 text-right font-mono">× {r.multiplier}</td>
                          <td className="py-1 text-right font-mono font-semibold">{brl(r.due_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Histórico */}
        {history.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Cálculos anteriores ({history.length})
            </div>
            <div className="space-y-1 text-xs text-slate-600">
              {history.map(h => (
                <div key={h.id} className="flex items-center gap-2">
                  <span className="font-mono">{new Date(h.generated_at).toLocaleString("pt-BR")}</span>
                  <span>·</span>
                  <span>{h.calc_start} → {h.calc_end}</span>
                  <span>·</span>
                  <b>{brl(h.total_cents)}</b>
                  <span>·</span>
                  <span className="font-mono text-slate-400">{h.engine_version}</span>
                  <button onClick={() => downloadPdf(h.id)}
                    className="ml-auto text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid={`liq-calc-history-pdf-${h.id}`}>
                    <Download className="w-3 h-3"/> PDF
                  </button>
                  <button onClick={() => shareCalcOnWhatsApp(h, caseData)}
                    className="text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1 font-medium"
                    data-testid={`liq-calc-history-wa-${h.id}`}
                    title="Compartilhar link com preview no WhatsApp">
                    <Share2 className="w-3 h-3"/> WhatsApp
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ParamDisplay({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-mono text-slate-900">{value}</div>
    </div>
  );
}
